/**
 * i18n инициализация для Luna Protokol.
 *
 * Порядок детекта языка:
 *   1. luna_user_settings_v1 из localStorage (явный выбор юзера)
 *   2. Telegram WebApp language_code (ru → ru; всё остальное → en)
 *   3. navigator.language (только если ru/en)
 *   4. Default: en
 *
 * При переключении через SettingsModal — вызывать
 * `i18n.changeLanguage(lng)` + `useUserSettings.setSettings({language:lng})`.
 * Синхронизация в обе стороны обеспечивается в main.tsx.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import translationRU from './locales/ru.json';
import translationEN from './locales/en.json';

const SETTINGS_KEY = 'luna_user_settings_v1';

type SupportedLang = 'ru' | 'en';

const resources = {
  ru: { translation: translationRU },
  en: { translation: translationEN },
};

function readSettingsLanguage(): SupportedLang | undefined {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { language?: string };
    if (parsed.language === 'ru' || parsed.language === 'en') {
      return parsed.language;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

function getTelegramLanguage(): SupportedLang {
  try {
    const tg = (window as unknown as {
      Telegram?: {
        WebApp?: { initDataUnsafe?: { user?: { language_code?: string } } };
      };
    }).Telegram?.WebApp?.initDataUnsafe?.user;
    if (tg?.language_code === 'ru') return 'ru';
    return 'en';
  } catch {
    return 'en';
  }
}

const lunaLanguageDetector = {
  name: 'lunaLanguageDetector',
  lookup(): SupportedLang {
    const saved = readSettingsLanguage();
    if (saved) return saved;

    const tgLang = getTelegramLanguage();
    if (tgLang) return tgLang;

    const browser = navigator.language.split('-')[0];
    if (browser === 'ru' || browser === 'en') return browser;

    return 'en';
  },
  cacheUserLanguage() {
    /* кэшируем через useUserSettings, здесь no-op */
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(lunaLanguageDetector);

i18n
  .use(initReactI18next)
  .use(languageDetector)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['ru', 'en'],

    detection: {
      order: ['lunaLanguageDetector'],
      caches: [],
    },

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: false,
    },
  });

export type { SupportedLang };
export default i18n;
